/** DB/API may return numeric columns as strings; use this before comparing to 0. */
export function isFreePlan(plan: Pick<Plan, 'name' | 'priceMonthly'> | null | undefined): boolean {
  if (!plan) return false;
  if (plan.name?.toUpperCase() === 'FREE') return true;
  const m = Number(plan.priceMonthly);
  return Number.isFinite(m) && m === 0;
}

export interface Plan {
  id: string;
  name: string;
  displayName: string;
  priceMonthly: number;
  priceYearly: number;
  maxSites: number;
  maxPagesPerSite: number;
  customDomain: boolean;
  contactForms: boolean;
  advancedComponents: boolean;
  competeStudio: boolean;
  features: string[];
}

export interface CurrentSubscription {
  subscription?: {
    id: string;
    planId: string;
    planName?: string;
    status: string;
    billingPeriod: 'monthly' | 'yearly';
    currentPeriodEnd: string;
    canceledAt?: string | null;
    stripeCustomerId?: string;
  } | null;
  plan?: Plan;
  usage?: {
    sites: number;
    pages: number;
    plan?: Plan;
  };
  limits?: {
    maxSites: number;
    maxPagesPerSite: number;
    canUseDomain: boolean;
    canUseContactForms: boolean;
    canUseAdvancedComponents: boolean;
    canUseCompeteStudio: boolean;
  };
}

export const DEFAULT_PLANS: Plan[] = [
  {
    id: 'free-plan',
    name: 'FREE',
    displayName: 'Free',
    priceMonthly: 0,
    priceYearly: 0,
    maxSites: 1,
    maxPagesPerSite: 5,
    customDomain: false,
    contactForms: false,
    advancedComponents: false,
    competeStudio: false,
    features: [
      '1 Website',
      'Up to 5 Pages per Site',
      'Basic Components',
      'Free Subdomain',
      'SSL Certificate',
      'Basic Support',
    ],
  },
  {
    id: 'starter-plan',
    name: 'STARTER',
    displayName: 'Starter',
    priceMonthly: 29,
    priceYearly: 290,
    maxSites: 2,
    maxPagesPerSite: 20,
    customDomain: true,
    contactForms: true,
    advancedComponents: false,
    competeStudio: false,
    features: [
      '2 Websites',
      'Up to 20 Pages per Site',
      'Custom Domain',
      'Contact Forms',
      'Priority Support',
      'Analytics Dashboard',
    ],
  },
  {
    id: 'professional-plan',
    name: 'PROFESSIONAL',
    displayName: 'Professional',
    priceMonthly: 99,
    priceYearly: 990,
    maxSites: 5,
    maxPagesPerSite: -1,
    customDomain: true,
    contactForms: true,
    advancedComponents: true,
    competeStudio: true,
    features: [
      '5 Websites',
      'Unlimited Pages',
      'Custom Domain',
      'Advanced Components',
      'Compete Studio Access',
      'Priority Support',
      'Advanced Analytics',
      'Custom Integrations',
    ],
  },
];

// Helper to derive plan from subscription data
export const derivePlanFromSubscription = (
  planId: string | undefined,
  planName: string | undefined,
  loadedPlans: Plan[]
): Plan | null => {
  if (!planId && !planName) return null;

  // Known plan GUIDs
  const STARTER_PLAN_GUID = '13937E9E-1575-4B8A-94B9-B10858967049';

  // Try to find by ID first
  let derivedPlan = loadedPlans.find(p => p.id === planId);

  if (!derivedPlan && loadedPlans.length > 0 && planId) {
    if (planId === STARTER_PLAN_GUID) {
      derivedPlan = loadedPlans.find(p => p.name === 'STARTER') || DEFAULT_PLANS[1];
    }
  }

  // If still not found, try by name
  if (!derivedPlan && planName) {
    const upperName = planName.toUpperCase();
    if (upperName.includes('STARTER')) {
      derivedPlan = loadedPlans.find(p => p.name === 'STARTER') || DEFAULT_PLANS[1];
    } else if (upperName.includes('PROFESSIONAL')) {
      derivedPlan = loadedPlans.find(p => p.name === 'PROFESSIONAL') || DEFAULT_PLANS[2];
    } else {
      derivedPlan = loadedPlans.find(p => p.name === 'FREE') || DEFAULT_PLANS[0];
    }
  }

  return derivedPlan ?? null;
};
